import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Heart, ChevronLeft, Check, X, Sparkles, Coins, Star, Zap, Timer } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { MathText } from "../components/MathText";

const QUESTION_TIME = 30;

export default function Quiz() {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const tr = useT();
  const studyMode = levelId.startsWith("exam_") || levelId.startsWith("plan_");
  const noTimer = levelId.startsWith("exam_");
  const [source, setSource] = useState(null); // choose static/ai first
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [hearts, setHearts] = useState(noTimer ? 99 : 3);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState([]);
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [result, setResult] = useState(null);
  const [time, setTime] = useState(QUESTION_TIME);

  useEffect(() => {
    if (studyMode && !source) loadQuiz("static");
    // eslint-disable-next-line
  }, [levelId]);

  const loadQuiz = async (src) => {
    setSource(src);
    setLoading(true);
    try {
      const { data } = await api.get(`/quiz/${levelId}?source=${src}`);
      setQuiz(data);
      setTime(QUESTION_TIME);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate(-1);
    } finally { setLoading(false); }
  };

  const answer = useCallback((choice) => {
    if (revealed || !quiz) return;
    setPicked(choice);
    setRevealed(true);
    const isCorrect = choice === quiz.answers[idx];
    if (isCorrect) setCorrect((c) => c + 1);
    else {
      setHearts((h) => h - 1);
      setWrong((w) => [...w, { q: quiz.questions[idx].q, o: quiz.questions[idx].o, c: quiz.answers[idx], e: quiz.explanations[idx] }]);
    }
  }, [revealed, quiz, idx]);

  // per-question timer
  useEffect(() => {
    if (!quiz || revealed || result || noTimer) return;
    if (time <= 0) { answer(-1); return; }
    const t = setTimeout(() => setTime((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [time, quiz, revealed, result, answer, noTimer]);

  const finish = async (finalCorrect) => {
    try {
      const { data } = await api.post("/quiz/submit", {
        level_id: levelId, correct: finalCorrect, total: quiz.questions.length,
        subject: quiz.subject, wrong_questions: wrong, is_ai: source === "ai",
      });
      setResult(data);
      setUser(data.user);
      (data.new_badges || []).forEach((b) => toast.success(`🏆 ${tr("Traguardi")}: ${b.name}`));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const next = () => {
    const finalCorrect = correct;
    if (hearts <= 0) { finish(finalCorrect); return; }
    if (idx + 1 >= quiz.questions.length) { finish(finalCorrect); return; }
    setIdx(idx + 1); setPicked(null); setRevealed(false); setTime(QUESTION_TIME);
  };

  // --- Source picker ---
  if (!source) {
    return (
      <div className="space-bg min-h-screen flex flex-col items-center justify-center px-5">
        <button onClick={() => navigate(-1)} className="absolute top-5 left-5 text-white/60 bouncy flex items-center gap-1" data-testid="quiz-back-btn"><ChevronLeft size={18} /> {tr("Indietro")}</button>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-6 w-full max-w-md text-center">
          <Sparkles size={40} style={{ color: "var(--cyan)" }} className="mx-auto mb-3" />
          <h2 className="font-heading font-black text-2xl tracking-tight mb-2">{tr("Scegli la modalita")}</h2>
          <p className="text-white/60 mb-6 text-sm">{tr("Domande curate o generate al volo dall'AI.")}</p>
          <div className="space-y-3">
            <button onClick={() => loadQuiz("static")} className="w-full py-4 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="mode-static-btn">{tr("📚 Domande classiche")}</button>
            <button onClick={() => loadQuiz("ai")} className="w-full py-4 rounded-2xl btn-violet font-heading font-bold bouncy" data-testid="mode-ai-btn">{tr("✨ Sfida AI (nuove domande)")}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading || !quiz) {
    return <div className="space-bg min-h-screen flex flex-col items-center justify-center gap-3 text-white/70">
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}><Sparkles size={36} style={{ color: "var(--cyan)" }} /></motion.div>
      {source === "ai" ? tr("L'AI sta creando le tue domande...") : tr("Caricamento quiz...")}
    </div>;
  }

  // --- Result screen ---
  if (result) {
    return (
      <div className="space-bg min-h-screen flex flex-col items-center justify-center px-5">
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass rounded-3xl p-8 w-full max-w-md text-center" data-testid="result-card">
          <h2 className="font-heading font-black text-3xl tracking-tight mb-1">{result.passed ? tr("Livello superato!") : tr("Riprova!")}</h2>
          <p className="text-white/60 mb-5">{result.passed ? (result.is_boss ? tr("Boss sconfitto! 🪐") : tr("Ottimo lavoro 🚀")) : tr("Non mollare, ci sei quasi.")}</p>
          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <motion.div key={s} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 + s * 0.15, type: "spring" }}>
                <Star size={44} className={s <= result.stars ? "fill-current" : ""} style={{ color: s <= result.stars ? "var(--gold)" : "rgba(255,255,255,0.2)" }} />
              </motion.div>
            ))}
          </div>
          <div className="flex justify-center gap-6 font-num mb-6">
            <div className="flex items-center gap-1"><Zap size={20} style={{ color: "var(--cyan)" }} /> +{result.xp_earned} XP</div>
          </div>
          <div className="space-y-3">
            <button onClick={() => navigate(-1)} className="w-full py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="result-continue-btn">{tr("Continua")}</button>
            <button onClick={() => window.location.reload()} className="w-full py-2 rounded-2xl text-white/60 bouncy text-sm" data-testid="result-retry-btn">{tr("Rigioca livello")}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  // --- Quiz gameplay ---
  const q = quiz.questions[idx];
  return (
    <div className="space-bg min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-white/60 bouncy" data-testid="quiz-exit-btn"><X size={22} /></button>
        <div className="flex-1 mx-4 h-3 rounded-full bg-black/40 overflow-hidden">
          <motion.div className="h-full rounded-full" style={{ background: "var(--cyan)", boxShadow: "0 0 10px var(--cyan)" }}
            animate={{ width: `${((idx) / quiz.questions.length) * 100}%` }} />
        </div>
        <div className="flex gap-1" data-testid="quiz-hearts">
          {studyMode ? (
            <span className="text-xs text-white/50 font-heading uppercase tracking-wider">{tr("Studio")}</span>
          ) : [1, 2, 3].map((h) => (
            <Heart key={h} size={20} className={h <= hearts ? "fill-current" : ""} style={{ color: h <= hearts ? "var(--pink)" : "rgba(255,255,255,0.2)" }} />
          ))}
        </div>
      </header>

      <div className="flex-1 max-w-lg w-full mx-auto px-4 py-6 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs uppercase tracking-widest text-white/40 font-heading">{quiz.topic} · {idx + 1}/{quiz.questions.length}</span>
          {!noTimer && <span className={`flex items-center gap-1 font-num ${time <= 5 ? "text-red-400" : "text-white/60"}`} data-testid="quiz-timer"><Timer size={16} /> {time}s</span>}
        </div>

        <motion.div key={idx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
          className="glass rounded-3xl p-6 mb-5" data-testid="question-card">
          <p className="font-heading font-bold text-lg tracking-tight"><MathText>{q.q}</MathText></p>
        </motion.div>

        <div className="space-y-3 flex-1">
          {q.o.map((opt, oi) => {
            let cls = "glass text-white";
            if (revealed) {
              if (oi === quiz.answers[idx]) cls = "text-white";
              else if (oi === picked) cls = "text-white";
            }
            const isCorrect = revealed && oi === quiz.answers[idx];
            const isWrong = revealed && oi === picked && oi !== quiz.answers[idx];
            return (
              <motion.button key={oi} whileTap={{ scale: 0.97 }} onClick={() => answer(oi)} disabled={revealed}
                className={`w-full text-left px-5 py-4 rounded-2xl font-body font-semibold flex items-center justify-between ${cls}`}
                style={{
                  border: isCorrect ? "2px solid var(--success)" : isWrong ? "2px solid var(--danger)" : "1px solid rgba(255,255,255,0.12)",
                  background: isCorrect ? "rgba(0,255,127,0.15)" : isWrong ? "rgba(255,51,102,0.15)" : undefined,
                }}
                data-testid={`answer-${oi}`}>
                <span><MathText>{opt}</MathText></span>
                {isCorrect && <Check size={20} style={{ color: "var(--success)" }} />}
                {isWrong && <X size={20} style={{ color: "var(--danger)" }} />}
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence>
          {revealed && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="glass rounded-2xl p-4 mt-4" data-testid="explanation-box">
              <p className="text-sm text-white/70"><b style={{ color: picked === quiz.answers[idx] ? "var(--success)" : "var(--danger)" }}>{picked === quiz.answers[idx] ? tr("Corretto! ") : tr("Spiegazione: ")}</b><MathText>{quiz.explanations[idx]}</MathText></p>
              <button onClick={next} className="w-full mt-3 py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="next-btn">
                {hearts <= 0 || idx + 1 >= quiz.questions.length ? tr("Termina") : tr("Prossima")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
